// state/orderedReducer.ts
import { AnyAction } from 'redux';
import {
  OrderedOrderHeaderViewModel,
  OrderedCompanyViewModel,
  OrderedPageViewModel,
  OrderedContactViewModel,
  OrderedPurchaseLineViewModel,
} from '../models/orderedModel';

export interface IOrderedState {
  // meta
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;

  // data
  orders: OrderedOrderHeaderViewModel[]; // header + lines
  selectedOrder: OrderedOrderHeaderViewModel | null; // valgt ordre
  selectedVendor: OrderedCompanyViewModel | null; // valgt leverandør
  selectedVendorContact: OrderedContactViewModel | null; // valgt leverandørkontakt
}

export const initialOrderedState: IOrderedState = {
  page: 1,
  pageSize: 25,
  total: 0,
  hasMore: false,
  orders: [],
  selectedOrder: null,
  selectedVendor: null,
  selectedVendorContact: null,
};

/* ---------------- Actions ---------------- */

type UpdateLineFieldsPayload = {
  // match
  orderPurchaseLineId: string;

  // patch – kun felter vi faktisk støtter på linjen
  receivedQuantity?: number;
  unitPrice?: number;
  retailPrice?: number;
  vendorProductUrl?: string | null;
  status?: number | string; // hvis UI sender string, coerces til number
  deliveryDate?: string | null; // ISO
};

export type OrderedActions =
  | { type: 'ORDERED/REQUEST'; payload?: { page?: number; pageSize?: number } }
  | { type: 'ORDERED/LOAD_PAGE'; payload: OrderedPageViewModel }
  | { type: 'ORDERED/RESET'; payload?: {} }
  | {
      type: 'ORDERED/SET_ORDERS';
      payload: { data: OrderedOrderHeaderViewModel[] };
    }
  | {
      type: 'ORDERED/SET_SELECTED_ORDER';
      payload: { order: OrderedOrderHeaderViewModel | null };
    }
  | {
      type: 'ORDERED/UPDATE_ORDER_LIST';
      payload: { order: OrderedOrderHeaderViewModel };
    }
  | {
      type: 'ORDERED/UPDATE_LINE_RECEIVED_QUANTITY';
      payload: { orderPurchaseLineId: string; receivedQuantity: number };
    }
  | {
      type: 'ORDERED/SET_SELECTED_VENDOR_CONTACT';
      payload: { vendorContact: OrderedContactViewModel | null };
    }
  | {
      type: 'ORDERED/SET_SELECTED_VENDOR';
      payload: { vendor: OrderedCompanyViewModel | null };
    }
  | {
      type: 'ORDERED/UPDATE_LINE_FIELDS';
      payload: UpdateLineFieldsPayload;
    };

/* ---------------- Helpers ---------------- */

// Behold valgt ordre hvis den fortsatt finnes i ny side
function resolveSelectedOrder(
  currentSelected: OrderedOrderHeaderViewModel | null,
  newOrders: OrderedOrderHeaderViewModel[]
): OrderedOrderHeaderViewModel | null {
  if (!newOrders?.length) return null;
  if (!currentSelected) return newOrders[0];
  const stillThere = newOrders.find(
    (o) => o.orderId === currentSelected.orderId
  );
  return stillThere ?? newOrders[0];
}

// Normaliser patch så det matcher OrderedPurchaseLineViewModel
function normalizeLinePatch(
  p: UpdateLineFieldsPayload
): Partial<OrderedPurchaseLineViewModel> {
  const out: Partial<OrderedPurchaseLineViewModel> = {};

  if (p.receivedQuantity !== undefined)
    out.receivedQuantity = p.receivedQuantity;
  if (p.unitPrice !== undefined) out.unitPrice = p.unitPrice;
  if (p.retailPrice !== undefined) out.retailPrice = p.retailPrice;

  if (p.vendorProductUrl !== undefined) {
    // null => undefined i state (unngå eksplisitt null når feltet er optional)
    (out as any).vendorProductUrl = p.vendorProductUrl ?? undefined;
  }

  if (p.status !== undefined) {
    (out as any).status =
      typeof p.status === 'string' ? Number(p.status) : p.status;
  }

  if (p.deliveryDate !== undefined) {
    out.deliveryDate = p.deliveryDate ?? undefined;
  }

  return out;
}

// Patch én linje om id matcher
function patchLineIfMatch(
  line: OrderedPurchaseLineViewModel,
  matchId: string,
  patch: Partial<OrderedPurchaseLineViewModel>
): OrderedPurchaseLineViewModel {
  if (line.orderPurchaseLineId !== matchId) return line;
  return { ...line, ...patch };
}

// Patch alle ordrer i listen (immutabelt)
function patchOrdersLinesList(
  orders: OrderedOrderHeaderViewModel[],
  matchId: string,
  patch: Partial<OrderedPurchaseLineViewModel>
): OrderedOrderHeaderViewModel[] {
  let changed = false;

  const next = orders.map((order) => {
    if (!order.lines || order.lines.length === 0) return order;

    let linesChanged = false;
    const nextLines = order.lines.map((ln) => {
      const nl = patchLineIfMatch(ln, matchId, patch);
      linesChanged = linesChanged || nl !== ln;
      return nl;
    });

    if (linesChanged) {
      changed = true;
      return { ...order, lines: nextLines };
    }

    return order;
  });

  return changed ? next : orders;
}

/* ---------------- Reducer ---------------- */

export const OrderedReducer = (
  state: IOrderedState = initialOrderedState,
  action: AnyAction
): IOrderedState => {
  const a = action as OrderedActions;

  switch (a.type) {
    case 'ORDERED/REQUEST': {
      const page = a.payload?.page ?? state.page;
      const pageSize = a.payload?.pageSize ?? state.pageSize;
      return { ...state, page, pageSize };
    }

    case 'ORDERED/LOAD_PAGE': {
      const data = a.payload;
      const orders = data.orders ?? [];

      const hasMore =
        typeof (data as any).hasMore === 'boolean'
          ? (data as any).hasMore
          : data.page * data.pageSize < data.total;

      const selectedOrder = resolveSelectedOrder(state.selectedOrder, orders);
      const selectedVendor = selectedOrder?.vendor ?? null;

      return {
        ...state,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
        hasMore,
        orders,
        selectedOrder,
        selectedVendor,
      };
    }

    case 'ORDERED/RESET': {
      return { ...initialOrderedState };
    }

    case 'ORDERED/SET_ORDERS': {
      const orders = a.payload.data ?? [];
      const selectedOrder = resolveSelectedOrder(state.selectedOrder, orders);
      const selectedVendor = selectedOrder?.vendor ?? null;
      return { ...state, orders, selectedOrder, selectedVendor };
    }

    case 'ORDERED/SET_SELECTED_ORDER': {
      const selectedOrder = a.payload.order;
      const selectedVendor = selectedOrder?.vendor ?? null;
      return { ...state, selectedOrder, selectedVendor };
    }

    case 'ORDERED/UPDATE_ORDER_LIST': {
      const updatedOrder = a.payload.order;
      const updatedOrders = state.orders.map((order) =>
        order.orderId === updatedOrder.orderId ? updatedOrder : order
      );

      const selectedOrder =
        state.selectedOrder?.orderId === updatedOrder.orderId
          ? updatedOrder
          : state.selectedOrder;

      const selectedVendor =
        selectedOrder?.vendor ?? state.selectedVendor ?? null;

      return { ...state, orders: updatedOrders, selectedOrder, selectedVendor };
    }

    case 'ORDERED/UPDATE_LINE_RECEIVED_QUANTITY': {
      const { orderPurchaseLineId, receivedQuantity } = a.payload;

      const patch: Partial<OrderedPurchaseLineViewModel> = {
        receivedQuantity,
      };

      // Oppdater liste
      const updatedOrders = patchOrdersLinesList(
        state.orders,
        orderPurchaseLineId,
        patch
      );

      // 2) Oppdater i selectedOrder (lines)
      let updatedSelectedOrder = state.selectedOrder;
      if (state.selectedOrder?.lines?.length) {
        const so = state.selectedOrder;
        const nextLines = so.lines.map((ln) =>
          patchLineIfMatch(ln, orderPurchaseLineId, patch)
        );
        const changed = nextLines.some((ln, i) => ln !== (so.lines ?? [])[i]);
        updatedSelectedOrder = changed ? { ...so, lines: nextLines } : so;
      }

      return {
        ...state,
        orders: updatedOrders,
        selectedOrder: updatedSelectedOrder,
      };
    }

    case 'ORDERED/UPDATE_LINE_FIELDS': {
      const { orderPurchaseLineId, ...rawPatch } =
        a.payload as UpdateLineFieldsPayload;

      const patch = normalizeLinePatch({
        orderPurchaseLineId,
        ...rawPatch,
      });

      // 1) Oppdater i liste
      const updatedOrders = patchOrdersLinesList(
        state.orders,
        orderPurchaseLineId,
        patch
      );

      // 2) Oppdater i selectedOrder (lines)
      let updatedSelectedOrder = state.selectedOrder;
      if (state.selectedOrder?.lines?.length) {
        const so = state.selectedOrder;
        const nextLines = so.lines.map((ln) =>
          patchLineIfMatch(ln, orderPurchaseLineId, patch)
        );
        const changed = nextLines.some((ln, i) => ln !== (so.lines ?? [])[i]);
        updatedSelectedOrder = changed ? { ...so, lines: nextLines } : so;
      }

      // 3) Vendor kan ha endret seg i selectedOrder (behold samme logikk)
      const selectedVendor =
        updatedSelectedOrder?.vendor ?? state.selectedVendor ?? null;

      return {
        ...state,
        orders: updatedOrders,
        selectedOrder: updatedSelectedOrder,
        selectedVendor,
      };
    }

    case 'ORDERED/SET_SELECTED_VENDOR': {
      const selectedVendor = a.payload.vendor;
      return { ...state, selectedVendor };
    }

    case 'ORDERED/SET_SELECTED_VENDOR_CONTACT': {
      const selectedVendorContact = a.payload.vendorContact;
      return { ...state, selectedVendorContact };
    }

    default:
      return state;
  }
};
